import React, { useState, useEffect } from 'react';
import { ReactComponent as CrossIcon } from '../../assets/icons/cross.svg';
import { ReactComponent as KeyIcon } from '../../assets/icons/key.svg';
import { ReactComponent as DeleteIcon } from '../../assets/icons/delete.svg';
import Button from '../Button/Button';
import './EditUserModal.css';

interface EditUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (userId: string, userData: { nom: string; prenom: string; email: string; telephone: string; fonction: string; role?: string }) => void;
  userId: string;
  userName: string;
  userEmail: string;
  userPrenom?: string;
  userFonction?: string;
  userRole?: string;
  userTelephone?: string;
  onDeleteRequest?: (userId: string) => void;
  onResetPasswordRequest?: () => void;
  onSaveLoginEmail?: (newEmail: string, memberId: number) => Promise<void>;
  availableRoles?: { value: string; label: string }[];
  userLoginEmail?: string;
}

const EditUserModal: React.FC<EditUserModalProps> = ({
  isOpen,
  onClose,
  onSave,
  userId,
  userName,
  userEmail,
  userPrenom = '',
  userFonction = '',
  userRole = '',
  userTelephone = '',
  onDeleteRequest,
  onResetPasswordRequest,
  onSaveLoginEmail,
  availableRoles,
  userLoginEmail = ''
}) => {
  const [nom, setNom] = useState('');
  const [prenom, setPrenom] = useState('');
  const [email, setEmail] = useState('');
  const [telephone, setTelephone] = useState('');
  const [fonction, setFonction] = useState('');
  const [role, setRole] = useState('');
  const [loginEmail, setLoginEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [errors, setErrors] = useState<{ nom?: string; prenom?: string; email?: string; telephone?: string; fonction?: string; role?: string; loginEmail?: string }>({});

  // Reset form when user changes
  useEffect(() => {
    if (isOpen) {
      // userName contient le nom complet "Prenom Nom", il faut extraire le nom de famille
      const fullName = userName || '';
      const prenomValue = userPrenom || '';

      // Extraire le nom de famille en retirant le prénom du début
      let nomValue = fullName;
      if (prenomValue && fullName.startsWith(prenomValue)) {
        nomValue = fullName.substring(prenomValue.length).trim();
      }

      setNom(nomValue);
      setPrenom(prenomValue);
      setEmail((userEmail || '').toLowerCase());
      setTelephone(userTelephone || '');
      setFonction(userFonction || '');
      setRole(userRole || '');
      setLoginEmail((userLoginEmail || '').toLowerCase());
      setErrors({});
    }
  }, [isOpen, userName, userPrenom, userEmail, userTelephone, userFonction, userRole, userLoginEmail]);

  // Handle Escape key press
  useEffect(() => {
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEsc);

    return () => {
      window.removeEventListener('keydown', handleEsc);
    };
  }, [isOpen, onClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const validateForm = (): boolean => {
    const newErrors: { nom?: string; prenom?: string; email?: string; telephone?: string; fonction?: string; loginEmail?: string } = {};

    if (!nom.trim()) {
      newErrors.nom = 'Le nom est requis';
    }

    if (!prenom.trim()) {
      newErrors.prenom = 'Le prénom est requis';
    }

    if (!email.trim()) {
      newErrors.email = 'L\'email est requis';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = 'Format d\'email invalide';
    }

    if (!telephone.trim()) {
      newErrors.telephone = 'Le numéro doit comporter 10 chiffres';
    } else if (!/^\d{10}$/.test(telephone)) {
      newErrors.telephone = 'Le numéro doit comporter 10 chiffres';
    }

    if (onSaveLoginEmail) {
      if (!loginEmail.trim()) {
        newErrors.loginEmail = 'L\'adresse email de connexion est requise';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(loginEmail)) {
        newErrors.loginEmail = 'Format d\'email invalide';
      }
    } else if (loginEmail.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(loginEmail)) {
      newErrors.loginEmail = 'Format d\'email invalide';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validateForm()) return;
    setIsConfirmOpen(true);
  };

  const handleConfirmedSave = async () => {
    setIsSubmitting(true);
    try {
      await onSave(userId, { nom, prenom, email, telephone, fonction, role });
      if (loginEmail.trim() && onSaveLoginEmail && loginEmail.trim() !== (userLoginEmail || '').trim()) {
        await onSaveLoginEmail(loginEmail.trim(), parseInt(userId));
      }
      setIsConfirmOpen(false);
      onClose();
    } catch (error) {
      console.error('Erreur lors de la mise à jour:', error);
      alert(`Erreur: ${error instanceof Error ? error.message : 'Une erreur est survenue'}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" style={{ display: 'flex' }} onClick={onClose}>
      <div
        className="modal-content"
        style={{ maxWidth: isConfirmOpen ? '450px' : '800px', width: '90%' }}
        onClick={(e) => e.stopPropagation()}
      >
        {!isConfirmOpen ? (
          <>
            <div className="modal-header">
              <h2>Modifier les informations</h2>
              <button type="button" className="close-button" onClick={onClose}>
                <CrossIcon />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group">
                  <label>Prénom <span className="required-star">*</span></label>
                  <input
                    type="text"
                    value={prenom}
                    onChange={(e) => setPrenom(e.target.value)}
                    className={errors.prenom ? 'input-error' : ''}
                    placeholder="Prénom"
                  />
                  {errors.prenom && <span className="error-message">{errors.prenom}</span>}
                </div>
                <div className="form-group">
                  <label>Nom <span className="required-star">*</span></label>
                  <input
                    type="text"
                    value={nom}
                    onChange={(e) => setNom(e.target.value)}
                    className={errors.nom ? 'input-error' : ''}
                    placeholder="Nom"
                  />
                  {errors.nom && <span className="error-message">{errors.nom}</span>}
                </div>
              </div>
              <div className="form-row">
                {onSaveLoginEmail ? (
                  <div className="form-group">
                    <label>Adresse email de connexion <span className="required-star">*</span></label>
                    <input
                      type="email"
                      value={loginEmail}
                      onChange={(e) => setLoginEmail(e.target.value.toLowerCase())}
                      className={errors.loginEmail ? 'input-error' : ''}
                      placeholder="Email de connexion"
                    />
                    {errors.loginEmail && <span className="error-message">{errors.loginEmail}</span>}
                    {loginEmail.trim() && loginEmail.trim() !== (userLoginEmail || '').trim() && (
                      <p className="edit-user-login-email-warning">
                        ⚠️ Modifier cet email déconnectera le membre. Il devra vérifier sa nouvelle adresse.
                      </p>
                    )}
                  </div>
                ) : <div />}
                <div className="form-group">
                  <label>Adresse email de contact <span className="required-star">*</span></label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value.toLowerCase())}
                    className={errors.email ? 'input-error' : ''}
                    placeholder="email@example.com"
                  />
                  {!onSaveLoginEmail && (
                    <span style={{ fontSize: '1rem', color: '#6b7280', marginTop: '0.25rem', display: 'block' }}>
                      Email de contact uniquement — l'identifiant de connexion n'est pas modifié.
                    </span>
                  )}
                  {errors.email && <span className="error-message">{errors.email}</span>}
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Téléphone <span className="required-star">*</span></label>
                  <input
                    type="tel"
                    value={telephone}
                    onChange={(e) => setTelephone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                    className={errors.telephone ? 'input-error' : ''}
                    placeholder="0600000000"
                  />
                  {errors.telephone && <span className="error-message">{errors.telephone}</span>}
                </div>
                <div className="form-group">
                  <label>Fonction</label>
                  <input
                    type="text"
                    value={fonction}
                    onChange={(e) => setFonction(e.target.value)}
                    placeholder="Fonction"
                  />
                </div>
              </div>
              {availableRoles && availableRoles.length > 0 && (
                <div className="form-group">
                  <label>Rôle</label>
                  <select value={role} onChange={(e) => setRole(e.target.value)}>
                    {availableRoles.map(r => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
            <div className="modal-secondary-actions">
              <span className="modal-secondary-title">Actions sur le compte</span>
              <div className="modal-secondary-buttons">
                {onDeleteRequest && (
                  <Button
                    value="Supprimer"
                    variant="danger"
                    icon={<DeleteIcon />}
                    onClick={() => { onDeleteRequest(userId); onClose(); }}
                  />
                )}
                {onResetPasswordRequest && (
                  <Button
                    value="Réinitialiser MDP"
                    variant="secondary"
                    icon={<KeyIcon />}
                    onClick={() => { onResetPasswordRequest(); onClose(); }}
                  />
                )}
              </div>
            </div>
            <div className="modal-actions">
              <Button
                value="Annuler"
                variant="secondary"
                onClick={onClose}
              />
              <Button
                value="Enregistrer"
                variant="primary"
                onClick={handleSubmit}
              />
            </div>
          </>
        ) : (
          <>
            <div className="modal-header">
              <h2>Confirmer la modification</h2>
              <button type="button" className="close-button" onClick={() => setIsConfirmOpen(false)}>
                <CrossIcon />
              </button>
            </div>
            <div className="modal-body">
              <p>Êtes-vous sûr de vouloir modifier les informations de <strong>{prenom} {nom}</strong> ?</p>
            </div>
            <div className="modal-actions">
              <Button
                value="Annuler"
                variant="secondary"
                onClick={() => setIsConfirmOpen(false)}
              />
              <Button
                value={isSubmitting ? 'Enregistrement...' : 'Confirmer'}
                variant="primary"
                onClick={handleConfirmedSave}
                disabled={isSubmitting}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default EditUserModal;